import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Heart, MessageCircle } from "lucide-react";
import api from "../lib/api";

const timeAgo = (iso) => {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(iso).toLocaleDateString("en-AU");
};

const LABELS = {
  comment_like: "liked your comment",
  comment_reply: "replied to your comment",
  testimonial_like: "liked your testimonial",
  testimonial_comment: "commented on your testimonial",
  tcomment_like: "liked your comment on a testimonial",
  tcomment_reply: "replied to your comment on a testimonial",
};

const NotificationBell = ({ testid = "notification-bell" }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0 });
  const ref = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(() => api.get("/notifications").then(({ data }) => setData(data)).catch(() => {}), []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const close = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && data.unread) {
      await api.post("/notifications/read-all").catch(() => {});
      setData((d) => ({ ...d, unread: 0 }));
    }
  };

  const go = (n) => {
    setOpen(false);
    navigate(n.link || `/community#post-${n.post_id}`);
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggle} data-testid={testid} aria-label="Notifications" className="relative p-1 text-[#e0a82e] transition-colors hover:text-[#f2b93f]">
        <Bell className="h-6 w-6" />
        {data.unread > 0 && (
          <span data-testid={`${testid}-count`} className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#e0574c] px-1 text-[11px] font-600 text-white">
            {data.unread > 9 ? "9+" : data.unread}
          </span>
        )}
      </button>
      {open && (
        <div data-testid={`${testid}-dropdown`} className="absolute right-0 z-50 mt-2 w-80 max-w-[calc(100vw-2rem)] overflow-hidden rounded-md border border-white/10 bg-[#242424] shadow-2xl">
          <p className="border-b border-white/10 px-4 py-2.5 font-display text-sm uppercase tracking-wide text-[#e0a82e]">Notifications</p>
          {data.items.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-[#9a9a9a]">No notifications yet.</p>
          ) : (
            <ul className="max-h-96 overflow-y-auto">
              {data.items.map((n) => (
                <li key={n.id}>
                  <button onClick={() => go(n)} data-testid={`notification-item-${n.id}`} className={`flex w-full gap-3 px-4 py-3 text-left transition-colors hover:bg-white/5 ${n.read ? "" : "bg-[#e0a82e]/5"}`}>
                    {n.type.endsWith("_like")
                      ? <Heart className="mt-0.5 h-4 w-4 shrink-0 fill-[#e0574c] text-[#e0574c]" />
                      : <MessageCircle className="mt-0.5 h-4 w-4 shrink-0 text-[#e0a82e]" />}
                    <span className="min-w-0 text-sm text-[#d8d8d8]">
                      <span className="font-600 text-[#e0a82e]">{n.actor}</span> {LABELS[n.type] || "interacted with you"}
                      {n.snippet && <span className="block truncate text-xs text-[#9a9a9a]">"{n.snippet}"</span>}
                      <span className="block text-xs text-[#777]">{timeAgo(n.createdAt)}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
